<script setup>
/**
 * @fileoverview Button component based on shadcn-vue
 * @description Reusable button component with multiple variants and sizes
 * @example
 * <Button variant="default" size="md" @click="handleClick">Click me</Button>
 */
import { computed } from 'vue'
import { Primitive } from 'radix-vue'
import { cn } from '@/lib/utils'

const props = defineProps({
  variant: {
    type: String,
    default: 'default',
    validator: value =>
      ['default', 'destructive', 'outline', 'secondary', 'ghost', 'link'].includes(value)
  },
  size: {
    type: String,
    default: 'default',
    validator: value => ['default', 'sm', 'lg', 'icon'].includes(value)
  },
  as: {
    type: String,
    default: 'button'
  },
  asChild: {
    type: Boolean,
    default: false
  }
})

const buttonVariants = computed(() => {
  const baseStyles =
    'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50'

  const variants = {
    default: 'bg-primary text-primary-foreground hover:bg-primary/90',
    destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
    outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
    secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
    ghost: 'hover:bg-accent hover:text-accent-foreground',
    link: 'text-primary underline-offset-4 hover:underline'
  }

  const sizes = {
    default: 'h-10 px-4 py-2',
    sm: 'h-9 rounded-md px-3',
    lg: 'h-11 rounded-md px-8',
    icon: 'h-10 w-10'
  }

  return cn(baseStyles, variants[props.variant], sizes[props.size])
})
</script>

<template>
  <Primitive :as="as" :as-child="asChild" :class="buttonVariants">
    <slot />
  </Primitive>
</template>
